import { createFileRoute } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { DocsPageShell, type DocBlock } from "@/components/marketing/DocsLayout";

const blocks: DocBlock[] = [
  {
    type: "p",
    text: "Personality controls how the twin sounds and behaves on top of its grounded knowledge — the difference between a helpful expert and a robotic bot.",
  },
  { type: "h2", text: "Let AI draft it for you" },
  {
    type: "p",
    text: "You don't have to write the personality by hand. Reality Twin can generate a first-person system prompt from your indexed knowledge — voice, expertise, tone and how to answer inbound questions — in one click. Review it, tweak what reads wrong, and save.",
  },
  { type: "h2", text: "What you can shape" },
  {
    type: "ul",
    items: [
      "Voice & tone — from formal to playful, concise to thorough.",
      "How it qualifies — the questions it asks to understand a visitor's need and fit.",
      "Handoff behaviour — when and how it offers to connect a human or take contact details.",
      "Boundaries — topics to avoid and how to refuse gracefully.",
    ],
  },
  { type: "h2", text: "How the twin behaves by default" },
  {
    type: "p",
    text: "Every twin is instructed to advise like a senior human expert at your company: be specific and grounded, ask one sharp clarifying question when a need is ambiguous, recommend the right next step, and be honest about limits instead of inventing facts. It replies in whatever language the visitor writes in.",
  },
  {
    type: "callout",
    title: "Citations are automatic",
    text: "When the twin uses a knowledge excerpt, it cites it inline as [1], [2] — visitors (and you) can see exactly where an answer came from.",
  },
  { type: "h2", text: "Iterating" },
  {
    type: "p",
    text: "Personality changes go live immediately. Watch real conversations, adjust the voice and qualification questions, and re-generate from knowledge whenever your offer changes.",
  },
];

export const Route = createFileRoute("/docs/twin-personality")({
  head: () =>
    buildSeo({
      path: "/docs/twin-personality",
      title: "Twin Personality — Reality Twin Docs",
      description:
        "Tune voice, qualification and handoff — or let AI draft the personality from your knowledge.",
    }),
  component: () => (
    <DocsPageShell
      slug="twin-personality"
      title="Twin personality"
      description="Tune voice, behaviour and how the twin qualifies and hands off — or let AI draft it."
      blocks={blocks}
    />
  ),
});
